import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { LayoutDashboard, CalendarRange, Building2, Tag, Users, Trophy, Lock, Megaphone, FileSpreadsheet, ShieldCheck, LogOut, ArrowLeft, RefreshCcw, Award, GraduationCap, Settings, UserCog, KeyRound, Inbox, Image } from "lucide-react";
import BrandLogo from "../../components/BrandLogo";

const baseMenu = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
    { to: "/admin/bookings", label: "Bookings", icon: CalendarRange, testid: "admin-nav-bookings" },
    { to: "/admin/refunds", label: "Refunds", icon: RefreshCcw, testid: "admin-nav-refunds" },
    { to: "/admin/slots", label: "Slots", icon: Lock, testid: "admin-nav-slots" },
    { to: "/admin/events", label: "Events", icon: Trophy, testid: "admin-nav-events" },
    { to: "/admin/academy", label: "Academy", icon: GraduationCap, testid: "admin-nav-academy" },
    { to: "/admin/centres", label: "Centres", icon: Building2, testid: "admin-nav-centres" },
    { to: "/admin/coupons", label: "Coupons", icon: Tag, testid: "admin-nav-coupons" },
    { to: "/admin/customers", label: "Customers", icon: Users, testid: "admin-nav-customers" },
    { to: "/admin/loyalty", label: "Loyalty", icon: Award, testid: "admin-nav-loyalty" },
    { to: "/admin/managers", label: "Managers", icon: ShieldCheck, testid: "admin-nav-managers" },
    { to: "/admin/broadcasts", label: "Broadcasts", icon: Megaphone, testid: "admin-nav-broadcasts" },
    { to: "/admin/banners", label: "Banners", icon: Image, testid: "admin-nav-banners" },
    { to: "/admin/contact-messages", label: "Inbox", icon: Inbox, testid: "admin-nav-inbox" },
    { to: "/admin/reports", label: "Reports", icon: FileSpreadsheet, testid: "admin-nav-reports" },
    { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
];

// Super-admin-only links (Mod 1 + Mod 2)
const superAdminMenu = [
    { to: "/admin/admins", label: "Admins", icon: UserCog, testid: "admin-nav-admins" },
    { to: "/admin/api-keys", label: "API Keys", icon: KeyRound, testid: "admin-nav-apikeys" },
];

export default function AdminLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const handleLogout = async () => { await logout(); navigate("/login"); };
    const isSuper = user?.role === "super_admin";
    const menu = isSuper ? [...baseMenu, ...superAdminMenu] : baseMenu;

    // Unread "open" contact-message count → badge on the Inbox link.
    const [openMsgs, setOpenMsgs] = useState(0);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/admin/contact-messages", { params: { limit: 500 } });
                if (!cancelled) setOpenMsgs((data || []).filter(m => (m.status || "open") === "open").length);
            } catch { /* ignore */ }
        };
        load();
        const id = setInterval(load, 60000); // refresh every 60s
        return () => { cancelled = true; clearInterval(id); };
    }, [location.pathname]);

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar */}
            <aside className="hidden md:flex w-64 flex-col bg-slate-950 border-r border-slate-800">
                <div className="p-5 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <BrandLogo size="md" />
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#00FF66]">Admin</div>
                    </div>
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {menu.map(({ to, label, icon: Icon, end, testid }) => {
                        const showBadge = to === "/admin/contact-messages" && openMsgs > 0;
                        return (
                            <NavLink key={to} to={to} end={end} data-testid={testid}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 h-10 rounded text-sm font-semibold transition-colors ${
                                        isActive ? "bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/30" : "text-slate-400 hover:text-white hover:bg-slate-900"
                                    }`
                                }>
                                <Icon className="w-4 h-4" /> <span className="flex-1">{label}</span>
                                {showBadge && (
                                    <span data-testid="inbox-unread-badge"
                                        className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-[18px] text-center">
                                        {openMsgs > 99 ? "99+" : openMsgs}
                                    </span>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="p-3 border-t border-slate-800 space-y-1">
                    <NavLink to="/" className="flex items-center gap-3 px-3 h-10 rounded text-sm text-slate-400 hover:text-white hover:bg-slate-900">
                        <ArrowLeft className="w-4 h-4" /> Player App
                    </NavLink>
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 h-10 rounded text-sm text-slate-400 hover:text-red-400 hover:bg-slate-900" data-testid="admin-logout-btn">
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800 h-14 flex items-center justify-between px-4">
                <div className="flex items-center gap-2">
                    <BrandLogo size="sm" />
                    <span className="font-display font-bold uppercase tracking-wider text-[#00FF66] text-[10px]">Admin</span>
                </div>
                <button onClick={handleLogout} className="text-slate-400" data-testid="admin-mobile-logout">
                    <LogOut className="w-4 h-4" />
                </button>
            </div>

            {/* Mobile bottom nav for admin */}
            <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-950/90 backdrop-blur-xl border-t border-slate-800">
                <ul className="flex overflow-x-auto h-16 no-scrollbar">
                    {menu.map(({ to, label, icon: Icon, end, testid }) => (
                        <li key={to} className="flex-shrink-0 w-[20%] min-w-[80px]">
                            <NavLink to={to} end={end} data-testid={`m-${testid}`}
                                className={({ isActive }) =>
                                    `flex flex-col items-center justify-center h-full gap-1 ${isActive ? "text-[#00FF66]" : "text-slate-400"}`
                                }>
                                <Icon className="w-4 h-4" />
                                <span className="text-[9px] font-bold uppercase tracking-widest">{label}</span>
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </nav>

            <main className="flex-1 md:ml-0 pt-14 md:pt-0 pb-20 md:pb-0 page-fade-in">
                {children}
            </main>
        </div>
    );
}
